import { endOfDay, startOfDay } from 'date-fns';
import { user$ } from '@/store/userStore';
import { DateRangePreset } from '@/types/DateRange.interface';
import { presetRange, REPORT_TIMEZONE } from '@/utils/salesReport';

/**
 * İŞLETME GÜNÜ — backend `date-filter.utils.ts` ile aynı sözleşme.
 *
 * Bir işletme günü 07:00'da başlar ve ERTESİ gün 03:00'da kapanır. Yani gece
 * 00:00–07:00 arasında hâlâ bir ÖNCEKİ işletme günü içindeyiz.
 *
 * Admin panelinin tarih presetleri (`Bugün`, `Dün`, `Son 7 gün`, …) çıplak
 * `new Date()` üzerinden kuruluyordu: gece 01:00'de "Bugün" YENİ takvim
 * gününü istiyor, backend bunu 07:00 → ertesi 03:00 olarak genişletiyor ve o
 * saatte yapılmış satışlar (önceki işletme gününe ait) ekrandan kayboluyordu.
 * Web POS ise aynı anda doğru günü gösteriyordu.
 *
 * NOT: `@date-fns/tz` bu projede bağımlılık DEĞİL; şube-yerel alanları
 * `Intl.DateTimeFormat` ile okuyoruz.
 */
export const BUSINESS_DAY_START_HOUR = 7;

/**
 * Şubenin IANA timezone'u; şube seçili değilse `Europe/Istanbul` — tarayıcının
 * saat dilimi DEĞİL (yurt dışından bakan yönetici başka gün kesiyordu). Web
 * POS ve backend varsayılanı da İstanbul.
 */
export function getBranchTimezone(): string {
  return user$.currentBranch.get()?.timezone ?? REPORT_TIMEZONE;
}

/** Verilen timezone'daki yerel takvim alanlarını okur. */
function localParts(timezone: string, at: Date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit'
  }).formatToParts(at);

  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value ?? '0');

  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    // en-CA saat 24 olarak gece yarısını verebilir; 0'a normalize et.
    hour: get('hour') % 24
  };
}

/** Saat diliminde şu anki saat (0–23). */
export function localHour(
  timezone: string = getBranchTimezone(),
  at: Date = new Date()
): number {
  return localParts(timezone, at).hour;
}

/**
 * ŞU AN içinde bulunduğumuz işletme günü, `yyyy-MM-dd` olarak.
 * Yerel saat 07:00'dan küçükse bir gün geri.
 */
export function businessDayToday(
  timezone: string = getBranchTimezone()
): string {
  const { year, month, day, hour } = localParts(timezone);
  const anchor = new Date(year, month - 1, day, 12, 0, 0, 0);
  if (hour < BUSINESS_DAY_START_HOUR) {
    anchor.setDate(anchor.getDate() - 1);
  }
  return toDateString(anchor);
}

function toDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * `yyyy-MM-dd` → o günün ÖĞLENİNE sabitli YEREL `Date`. Öğlen: hiçbir makul
 * saat kayması 12:00'yi gün sınırının öbür tarafına geçiremez, `startOfDay` /
 * `endOfDay` / `format(x, 'yyyy-MM-dd')` aynı günü verir.
 */
export function localDateFromString(date: string): Date {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

/**
 * Hızlı seçimin tarih aralığı (`DateFilterBar`, `useDateFilter`, rapor
 * sekmeleri). Aralık `utils/salesReport.ts` `presetRange`'ten gelir — web POS
 * ile aynı işletme günleri; tohum `businessDayToday`, çıplak `new Date()` değil.
 */
export function presetDateRange(
  preset: Exclude<DateRangePreset, 'custom'>,
  timezone: string = getBranchTimezone()
): { startDate: Date; endDate: Date } {
  const { startDate, endDate } = presetRange(
    preset,
    businessDayToday(timezone)
  );
  return {
    startDate: startOfDay(localDateFromString(startDate)),
    endDate: endOfDay(localDateFromString(endDate))
  };
}
